import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTeamMemberDetails } from './useTeamMemberDetails';
import {
  resolveTeamListRoleFromTeamState,
  viewerSeesOnlySelfInMemberPicker,
  filterMemberDetailsForSelect,
} from '../utils/memberSelectAccess';

/**
 * Same fetch/cache as {@link useTeamMemberDetails}, but `memberDetails` is scoped:
 * team **Member** → only the current user's row; **Manager** / **Admin** / other → full team.
 *
 * @param {string | null | undefined} teamId
 * @returns {object} Same as useTeamMemberDetails, plus viewerSeesOnlySelf and memberDetailsUnfiltered
 */
export function useTeamMemberDetailsForSelect(teamId) {
  const base = useTeamMemberDetails(teamId);
  const currentUserId = useSelector((s) => s.auth?.profile?.id);
  const teamState = useSelector((s) => s.team);

  const teamListRole = useMemo(
    () => resolveTeamListRoleFromTeamState(teamState, teamId),
    [teamState, teamId]
  );

  const viewerSeesOnlySelf = useMemo(
    () =>
      viewerSeesOnlySelfInMemberPicker({
        memberDetailsMap: base.memberDetails,
        currentUserId,
        teamListRole,
      }),
    [base.memberDetails, currentUserId, teamListRole]
  );

  const memberDetails = useMemo(
    () =>
      filterMemberDetailsForSelect(
        base.memberDetails,
        currentUserId,
        viewerSeesOnlySelf
      ),
    [base.memberDetails, currentUserId, viewerSeesOnlySelf]
  );

  return {
    ...base,
    memberDetails,
    memberDetailsUnfiltered: base.memberDetails,
    viewerSeesOnlySelf,
  };
}
